import { useCallback, useEffect, useState } from 'react';

import { LicenseDraftIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useNavigate } from 'react-router-dom';

import { AddRefereeCard } from './components/AddRefereeCard/AddRefereeCard';
import { CombinedReferees } from './components/CombinedReferees/CombinedReferees';
import { NewRefereeModal } from './components/NewRefereeModal/NewRefereeModal';
import { Referee } from './components/Referee/Referee';
import { RefereeCard } from './components/RefereeCard/RefereeCard';
import { SendRefereeRequestModal } from './components/SendRefereeRequestModal/SendRefereeRequestModal';
import Alert from 'components/Alert/Alert';
import { Form } from 'components/Form/Form/Form';
import { IconComponent } from 'components/Icon/IconComponent';

import { useGetFormsByTypeQuery } from 'api/forms-api';

import styles from './Reference.module.scss';

const COMBINED_ID = 0;

export const Reference = ({
  candidate,
  check,
  referees = [],
  company,
  brand,
  canAccessResults = true,
  messagePopUp = () => {},
  addReferee = () => {},
  setLoading = () => {},
  updateReferee = () => {},
  deleteReferee = () => {},
  cancelReferee = () => {},
  getPdfReport = () => {},
  getCombinedReport = () => {},
  sendRequestEmail = () => {},
  sendReplacementRequest = () => {},
}) => {
  const { getUpload, signUpload, createUpload, deleteUpload } = useApiUploadActions();
  const navigate = useNavigate();
  const { data: candidateForms } = useGetFormsByTypeQuery('type:candidate');
  const { data: refereeForms } = useGetFormsByTypeQuery('type:referee');

  const [selectedReferee, setSelectedReferee] = useState(null);
  const [showRequestAlert, setShowRequestAlert] = useState(false);
  const [showRefereeRequestEmailModal, setShowRefereeRequestEmailModal] = useState(false);
  const [showNewRefereeModal, setShowNewRefereeModal] = useState(false);
  const [candidateForm, setCandidateForm] = useState(null);
  const [refereeFormId, setRefereeFormId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpen = params.get('addReferee') === 'true';
    if (shouldOpen) {
      setShowNewRefereeModal(true);
    }
  }, [location.search, location.pathname, navigate]);

  const createCard = referee => (
    <RefereeCard
      key={referee.id}
      name={referee.submitted_at ? referee.name : 'Pending candidate nomination'}
      subtitle={
        <div className="d-flex align-items-center u-gap-small">
          <IconComponent icon={LicenseDraftIcon} noBackground size="x-small" type="secondary" />
          <span className="u-font-size-x-small u-font-weight-400 secondary-text">
            {referee.form_name}
          </span>
        </div>
      }
      referee={referee}
      selected={selectedReferee?.id === referee.id}
      disabled={referee.replaced_by || !referee.submitted_at}
      onClick={setSelectedReferee}
      referees={referees}
    />
  );

  const primaryCards = referees.filter(r => !r.backup).map(createCard);
  const backupCards = referees.filter(r => r.backup).map(createCard);

  const renderRefereeCards = (referees, allSameForm) => {
    const completedReferees = referees.filter(
      referee => !referee.replaced_by && !referee.canceled && referee.completed_at,
    );

    return (
      <div className="flex flex-col gap-8 w-1/4">
        {allSameForm && completedReferees.length > 1 && (
          <CombinedRefereeCard
            completedReferees={completedReferees}
            selectedReferee={selectedReferee}
            onSelect={setSelectedReferee}
          />
        )}
        <div className="flex flex-col gap-8">
          <p className="text-lg font-medium text-text-tertiary uppercase tracking-wide">
            Primary Referees
          </p>
          {primaryCards}
        </div>

        <AddRefereeCard onClick={() => setShowNewRefereeModal(true)} />

        {backupCards.length > 0 && <BackupRefereesSection backupCards={backupCards} />}
      </div>
    );
  };

  const clearAddRefereeParam = () => {
    const params = new URLSearchParams(location.search);
    params.delete('addReferee');
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
      replace: true,
    });
  };

  const handleNewRefereeFormSelection = async data => {
    switch (data.input_type) {
      case 'enter_details':
        setRefereeFormId(data.referee_form_id);
        const candidateForm = candidateForms.find(form => form.id === data.candidate_form_id);
        setCandidateForm(candidateForm);
        setShowNewRefereeModal(false);
        clearAddRefereeParam();
        break;
      case 'ask_candidate':
        setLoading(true);
        try {
          await addReferee({
            candidate_id: candidate.id,
            candidate_check_id: check.id,
            form_id: data.referee_form_id,
            candidate_form_id: data.candidate_form_id,
            manual: false,
            template_id: data.template_id,
          });
          setShowNewRefereeModal(false);
          setSelectedReferee(null);
          clearAddRefereeParam();
        } finally {
          setLoading(false);
        }
        break;
    }
  };

  const onNewReferee = async answers => {
    setLoading(true);
    const data = {
      candidate_id: candidate.id,
      candidate_check_id: check.id,
      candidate_form_id: candidateForm.id,
      form_id: refereeFormId,
      manual: true,
      answers,
    };
    const referee = await addReferee(data);
    setSelectedReferee(referee);
    setCandidateForm(null);
    setShowRequestAlert(true);
    setLoading(false);
  };

  const handleSendRefereeRequestEmail = async templateId => {
    setShowRefereeRequestEmailModal(false);
    await sendRequestEmail(selectedReferee.id, templateId);
  };

  const getBrandRefereeEmailTemplateId = useCallback(() => {
    return brand &&
      brand?.email_active &&
      brand?.email_referee_active &&
      brand?.email_referee_template_id
      ? brand?.email_referee_template_id
      : null;
  }, [brand]);

  return (
    <div className={cn(styles.root, 'bg-neutral-2 rounded-2xl border border-border-primary')}>
      <NewRefereeModal
        brand={brand}
        visible={showNewRefereeModal}
        candidateForms={candidateForms}
        brand={brand}
        refereeForms={refereeForms?.filter(form => !form.archived)}
        onSelect={handleNewRefereeFormSelection}
        onClose={() => {
          setShowNewRefereeModal(false);
          clearAddRefereeParam();
        }}
      />
      <div className="flex flex-col gap-2 p-8  bg-neutral-1 rounded-t-2xl border-b border-border-primary">
        <div className={styles.title}>Referees</div>
        <div className={styles.subtitle}>
          {referees?.length > 0
            ? 'View and update the list of referees provided by the candidate'
            : 'No referees have been provided'}
        </div>
      </div>
      <div className="flex flex-wrap gap-8 p-8">
        {renderRefereeCards(
          referees,
          referees.every(referee => referee['form_id'] === referees[0]['form_id']),
        )}
        {candidateForm && (
          <div className={cn('card', 'card-with-border', 'u-min-width-medium', 'u-padding')}>
            <h1 className="title-2 u-margin-bottom">{candidateForm.name}</h1>
            <Form
              candidateId={candidate.id}
              country={company.country}
              textParams={{
                candidate: candidate.first_name,
                candidateFirstName: candidate.first_name,
                candidateFullName: candidate.name,
                candidateLastName: candidate.last_name,
                job: candidate.job_role,
                companyName: brand?.brand_name ?? company?.name,
              }}
              form={candidateForm}
              brand={brand}
              buttonTitle="Create"
              onSubmit={onNewReferee}
              getCandidateUpload={getUpload}
              deleteCandidateUpload={deleteUpload}
              signCandidateUpload={signUpload}
              createCandidateUpload={createUpload}
            />
          </div>
        )}
        {selectedReferee?.id === COMBINED_ID && !candidateForm && (
          <CombinedReferees
            candidate={candidate}
            check={check}
            canAccessResults={canAccessResults}
            referees={referees.filter(
              referee => !referee.replaced_by && !referee.canceled && referee.completed_at,
            )}
            setLoading={setLoading}
            messagePopUp={messagePopUp}
            getPdfReport={getCombinedReport}
            onClose={() => setSelectedReferee(null)}
          />
        )}

        {selectedReferee && selectedReferee.id !== COMBINED_ID && !candidateForm && (
          <Referee
            brand={brand}
            candidate={candidate}
            company={company}
            check={check}
            referee={selectedReferee}
            canAccessResults={canAccessResults}
            setLoading={setLoading}
            messagePopUp={messagePopUp}
            updateReferee={updateReferee}
            deleteReferee={deleteReferee}
            cancelReferee={cancelReferee}
            getPdfReport={getPdfReport}
            setShowRequestAlert={() => setShowRequestAlert(true)}
            onNewRequest={() => setShowRefereeRequestEmailModal(true)}
            sendReplacementRequest={sendReplacementRequest}
            onClose={() => setSelectedReferee(null)}
          />
        )}
      </div>
      <Alert
        show={showRequestAlert}
        title="Would you like to send a new request to the referee?"
        ok="Send New Request"
        cancel="No Thanks"
        params={showRequestAlert}
        onOk={() => {
          setShowRequestAlert(false);
          setShowRefereeRequestEmailModal(true);
        }}
        onCancel={() => setShowRequestAlert(false)}
      />
      <SendRefereeRequestModal
        brand={brand}
        referee={selectedReferee}
        templateIdInBrandDefault={getBrandRefereeEmailTemplateId()}
        visible={showRefereeRequestEmailModal}
        onClose={() => setShowRefereeRequestEmailModal(false)}
        onSend={handleSendRefereeRequestEmail}
      />
    </div>
  );
};

const CombinedRefereeCard = ({ completedReferees, selectedReferee, onSelect }) => (
  <div>
    <RefereeCard
      combined
      title="Combined Report"
      name={completedReferees.map(referee => referee.name).join(' & ')}
      referee={{ id: COMBINED_ID }}
      onClick={onSelect}
      selected={selectedReferee?.id === COMBINED_ID}
    />
  </div>
);

const BackupRefereesSection = ({ backupCards }) => (
  <>
    <div className="u-divider" />
    <p>Backup Referees</p>
    {backupCards}
  </>
);
